/**
 * ScriptSureWindow.tsx
 *
 * The near-full-screen window every ScriptSure screen opens in, from the
 * patient Prescriptions tab and from the Prescribing page. ScriptSure lays drug
 * search, sig builder and pharmacy picker out side by side and needs a real
 * viewport, not a dialog-sized one.
 *
 * Built so work is not lost by accident (plan KTD18):
 *   - A backdrop click or Escape does not close it. Either could throw away a
 *     half-signed prescription; the header X still closes it.
 *   - The iframe has no `sandbox` attribute. ScriptSure opens controlled-
 *     substance two-step sign-in in a separate browser tab
 *     (docs/vendor/daw-scriptsure/supporting-material.md), and a sandbox
 *     blocks pop-ups, scripts and forms unless each is granted back.
 *   - It takes its address as a prop from the click that opened it and runs
 *     no query of its own: a refetch while it is open could swap the page
 *     under a prescriber mid-signing.
 *
 * ScriptSure's '200' signal (scriptSureMessage.ts) is honoured only from its
 * own origins and from this window's own iframe. It closes the prescribing
 * chart; every other screen stays open and only reports the signal, so the
 * caller can refresh the activity list.
 *
 * The full-application screen also offers a new browser window, for when that
 * sign-in's pop-up does not work from inside the frame.
 */
import { useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import type { DawWindowKind } from '@/services/prescriptions.service';
import { closesOnSignal, isScriptSureCloseSignal } from './scriptSureMessage';

/** Each window's heading, and the name its embedded screen carries for assistive technology. */
const WINDOW_TITLES: Record<DawWindowKind, { heading: string; frame: string }> = {
  chart: { heading: 'Prescribe', frame: 'Prescribing' },
  patient_messages: { heading: 'Messages', frame: 'Messages' },
  medication_history: { heading: 'Medication history', frame: 'Medication history' },
  prescribed_history: { heading: 'Prescription history', frame: 'Prescription history' },
  messages: { heading: 'Messages', frame: 'Messages' },
  pending: { heading: 'Pending prescriptions', frame: 'Pending prescriptions' },
  reports: { heading: 'Reports', frame: 'Reports' },
  audit_log: { heading: 'Audit log', frame: 'Audit log' },
  app: { heading: 'ScriptSure', frame: 'ScriptSure' },
};

/** The name the action that opens a window shows, so a button and its window read the same. */
export function scriptSureWindowLabel(kind: DawWindowKind): string {
  return WINDOW_TITLES[kind].heading;
}

export interface OpenScriptSureWindow {
  kind: DawWindowKind;
  /** The address the server issued for this opening. */
  url: string;
}

interface ScriptSureWindowProps {
  /** The screen on show, or null while the window is shut. */
  opened: OpenScriptSureWindow | null;
  onClose: () => void;
  /** ScriptSure posted its '200' from this window's own iframe. */
  onSignal?: () => void;
}

export function ScriptSureWindow({ opened, onClose, onSignal }: ScriptSureWindowProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  // The latest callbacks, read when a message arrives. Keeping them out of the
  // listener's effect attaches one listener per opening, however often the
  // caller re-renders with fresh callbacks.
  const onCloseRef = useRef(onClose);
  const onSignalRef = useRef(onSignal);
  useEffect(() => {
    onCloseRef.current = onClose;
    onSignalRef.current = onSignal;
  });

  const kind = opened?.kind ?? null;

  // Listening only while a window is open keeps exactly one listener alive at a
  // time, and the cleanup detaches it on close AND on unmount, so nothing
  // survives a remount.
  useEffect(() => {
    if (kind == null) return;
    const handleMessage = (event: MessageEvent) => {
      if (!isScriptSureCloseSignal(event, frameRef.current?.contentWindow)) return;
      onSignalRef.current?.();
      if (closesOnSignal(kind)) onCloseRef.current();
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [kind]);

  function openInNewWindow() {
    if (!opened) return;
    // No opener: the new window gets no handle back to this page. No referrer:
    // it is not told which page opened it.
    window.open(opened.url, '_blank', 'noopener,noreferrer');
  }

  const titles = opened ? WINDOW_TITLES[opened.kind] : null;

  return (
    <Modal
      isOpen={opened != null}
      onClose={onClose}
      title={titles?.heading}
      size="full"
      closeOnBackdrop={false}
      closeOnEscape={false}
      bodyClassName="!p-0 !overflow-hidden flex flex-col"
      footer={
        opened?.kind === 'app' ? (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<ExternalLink className="h-3.5 w-3.5" />}
            onClick={openInNewWindow}
          >
            Open in a new window
          </Button>
        ) : undefined
      }
    >
      {opened && titles && (
        <iframe
          ref={frameRef}
          title={titles.frame}
          src={opened.url}
          frameBorder={0}
          className="min-h-0 w-full flex-1"
        />
      )}
    </Modal>
  );
}
